import { useEffect } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import { Redirect } from "react-router-dom";
import { useAppSelector } from "../app/appHooks";

import { DevicesUnitList } from "./DevicesUnitList";
import { FloatingNotificationPanel } from "../common/FloatingNotificationPanel";
import {
  loadDevicesIssues,
  loadDevicesData,
  loadGatewaysData,
} from "./devicesThunks";
import { setSelectedUnit } from "./devicesSlice";
import { CommunityTypes, RoleTypes, Views } from "../../helpers/constants";
import { DevicesViewer } from "./DevicesViewer";
import { minToolbarHeight } from "../app/App";

const useStyles = makeStyles<Theme, Boolean>((theme: Theme) =>
  createStyles({
    devicesContainer: {
      display: "flex",
      height: (showBanner) =>
        `calc(100vh - ${minToolbarHeight(showBanner, theme)})`,
    },
    devicesViewerContainer: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      width: "100%",
      gap: "50px",
      padding: theme.spacing(6),
      overflowY: "auto",
      overflowX: "hidden",
      "@media (max-height: 830px)": {
        overflowY: "auto",
      },
      [theme.breakpoints.down("md")]: {
        overflowY: "auto",
      },

      [theme.breakpoints.down("md")]: {
        overflowX: "scroll",
      },
    },
  })
);

export default function Devices() {
  const showBanner = useAppSelector((state) => state.headerState.showBanner);

  /* ---- Hooks ---- */
  const classes = useStyles(showBanner);
  const dispatch = useDispatch();

  /* ---- Selectors ---- */
  const communityID = useAppSelector(
    (state) => state.headerState.selectedCommunity?.id
  );

  const selectedUnit = useAppSelector(
    (state) => state.devicesState.selectedUnit
  );

  const installerRole = useAppSelector(
    (state) => state.headerState.installerRole
  );
  const isAipCommunity = useAppSelector(
    (state) =>
      state.headerState.selectedCommunity?.community_type === CommunityTypes.aip
  );
  const isFamily = useAppSelector(
    (state) =>
      !!state.headerState.selectedCommunity?.role.includes(RoleTypes.family)
  );
  const units = useAppSelector((state) => state.headerState.units);

  /* ---- Effects ---- */
  // Auto-select the single unit for AIP communities
  useEffect(() => {
    if (isAipCommunity && units && units.length > 0 && !selectedUnit) {
      dispatch(setSelectedUnit(units[0].id));
    }
  }, [isAipCommunity, units, selectedUnit, dispatch]);

  // Fetch devices status
  useEffect(() => {
    if (communityID !== undefined && !isFamily) {
      dispatch(loadDevicesIssues(communityID));
    }
  }, [communityID, dispatch, isFamily]);

  useEffect(() => {
    if (selectedUnit !== undefined && !isFamily) {
      dispatch(loadDevicesData(selectedUnit));
      dispatch(loadGatewaysData(selectedUnit));
    }
  }, [dispatch, selectedUnit, isFamily]);

  if (isFamily) {
    return <Redirect to={Views.ANALYTICS} />;
  }

  return (
    <div className={classes.devicesContainer}>
      {!isAipCommunity && <DevicesUnitList />}
      <div className={classes.devicesViewerContainer}>
        <DevicesViewer />
      </div>
      {!installerRole && <FloatingNotificationPanel />}
    </div>
  );
}
